import { X } from 'lucide-react';
import { type ComponentType, useMemo, useState } from 'react';
import type { GroupBase, SingleValue, StylesConfig } from 'react-select';
import Select from 'react-select';

import { BULK_DEVICE_PAGE_SIZE } from '../../constants/pagination.js';
import { useDevices, useIntl } from '../../hooks';
import {
  useAddDeviceLink,
  useDeviceLinks,
  useRemoveDeviceLink,
} from '../../hooks/useDeviceLinks.js';
import { DeviceType } from '../../types';
import { Badge } from '../ui';

interface DeviceLinkedDevicesManagerProps {
  deviceId: string;
  disabled?: boolean;
}

interface DeviceOption {
  value: string;
  label: string;
}

type SelectProps = React.ComponentProps<
  typeof Select<DeviceOption, false, GroupBase<DeviceOption>>
>;
const SelectComponent = Select as unknown as ComponentType<SelectProps>;

const selectStyles: StylesConfig<DeviceOption, false, GroupBase<DeviceOption>> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 6,
    borderColor: 'var(--color-border)',
    color: 'var(--color-foreground)',
    boxShadow: state.isFocused ? '0 0 0 1px #0f172a' : 'none',
    ':hover': {
      borderColor: 'var(--color-border)',
      cursor: 'pointer',
    },
    backgroundColor: state.isDisabled ? 'var(--color-background-dark)' : 'var(--color-input)',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 30,
    backgroundColor: 'var(--color-background-light)',
    border: '1px solid var(--color-border)',
  }),
  option: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-background-light)',
    color: 'var(--color-foreground)',
    ':hover': {
      backgroundColor: 'var(--color-background-dark)',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--color-foreground)',
  }),
};

export function DeviceLinkedDevicesManager({
  deviceId,
  disabled = false,
}: DeviceLinkedDevicesManagerProps) {
  const intl = useIntl();
  const [message, setMessage] = useState<string | null>(null);

  const linksQuery = useDeviceLinks(deviceId);
  const devicesQuery = useDevices({ pageSize: BULK_DEVICE_PAGE_SIZE });
  const addLink = useAddDeviceLink(deviceId);
  const removeLink = useRemoveDeviceLink(deviceId);

  const links = useMemo(() => linksQuery.data ?? [], [linksQuery.data]);
  const allDevices = useMemo(() => devicesQuery.data?.devices ?? [], [devicesQuery.data]);

  const linkedDeviceIds = useMemo(() => new Set(links.map((l) => l.linkedDeviceId)), [links]);

  const deviceById = useMemo(() => new Map(allDevices.map((d) => [d.id, d])), [allDevices]);

  const availableOptions: DeviceOption[] = useMemo(() => {
    return allDevices
      .filter((d) => {
        if (d.id === deviceId) return false;
        if (linkedDeviceIds.has(d.id)) return false;
        const type = d.settings?.deviceType;
        return type === DeviceType.DETECTOR || type === DeviceType.DETECTOR_SUPPRESSOR;
      })
      .map((d) => ({
        value: d.id,
        label: d.model ? `${d.serial} (${d.model})` : d.serial,
      }));
  }, [allDevices, deviceId, linkedDeviceIds]);

  const handleSelect = async (option: SingleValue<DeviceOption>) => {
    if (!option) return;
    setMessage(null);
    try {
      await addLink.mutateAsync(option.value);
    } catch (err) {
      setMessage(
        err instanceof Error
          ? err.message
          : intl.formatMessage({ id: 'deviceLinkedDevices.addError' }),
      );
    }
  };

  const handleRemove = async (linkId: string, serial: string) => {
    setMessage(null);
    try {
      await removeLink.mutateAsync(linkId);
    } catch (err) {
      setMessage(
        err instanceof Error
          ? err.message
          : intl.formatMessage({ id: 'deviceLinkedDevices.removeError' }, { serial }),
      );
    }
  };

  const isLoading = linksQuery.isFetching || devicesQuery.isFetching;
  const isMutating = addLink.isPending || removeLink.isPending;

  return (
    <div className="space-y-3">
      {links.length === 0 ? (
        <div className="text-sm text-foreground">
          {intl.formatMessage({ id: 'deviceLinkedDevices.noLinkedDevices' })}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {links.map((link) => {
            const device = deviceById.get(link.linkedDeviceId);
            const serial = device
              ? device.model
                ? `${device.serial} (${device.model})`
                : device.serial
              : link.linkedDeviceId;
            return (
              <Badge
                key={link.id}
                variant="secondary"
                className="flex items-center gap-1 pr-1 text-xs"
                data-testid="linked-device-badge"
                data-linked-device-id={link.linkedDeviceId}
              >
                <span>{serial}</span>
                <button
                  type="button"
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-slate-200"
                  data-testid="remove-linked-device-button"
                  aria-label={intl.formatMessage(
                    { id: 'deviceLinkedDevices.removeDevice' },
                    { serial },
                  )}
                  onClick={() => void handleRemove(link.id, serial)}
                  disabled={disabled || isMutating}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      <SelectComponent
        value={null}
        onChange={(option) => void handleSelect(option)}
        options={availableOptions}
        isLoading={isLoading || isMutating}
        isDisabled={disabled || isMutating}
        placeholder={intl.formatMessage({ id: 'deviceLinkedDevices.placeholder' })}
        noOptionsMessage={() =>
          intl.formatMessage({ id: 'deviceLinkedDevices.noAvailableDevices' })
        }
        isClearable
        classNamePrefix="device-linked-devices-select"
        components={{ IndicatorSeparator: null }}
        styles={selectStyles}
        inputId="device-linked-devices-select"
        instanceId="device-linked-devices-select"
      />

      {message ? <div className="text-sm text-destructive">{message}</div> : null}
    </div>
  );
}
